import React, { useState, useEffect, useContext, useRef } from 'react';
import '../../assets/css/ngodashboard/Ngomessage.css';
import { Search, X, AlertCircle, CheckCircle, Inbox, Loader, Send } from 'lucide-react';
import NGOMasterLayout from '../../components/layout/NGOMasterLayout';
import AppContext from '../../context/AppContext';

const STATUS_CONFIG = {
  pending: { label: "Pending", cls: "badge-pending" },
  open:    { label: "Open",    cls: "badge-open"    },
  closed:  { label: "Closed",  cls: "badge-closed"  },
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-PK", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function formatTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status }) {
  const s = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <span className={`ngo-hs-badge ${s.cls}`}>{s.label}</span>;
}

/* ─── CHAT BOX ────────────────────────────────── */
function ChatBox({ ticket, baseUrl, onTicketUpdate }) {
  const [messages, setMessages]   = useState([]);
  const [newMsg, setNewMsg]       = useState('');
  const [sending, setSending]     = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, [ticket.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setLoadingMsgs(true);
    try {
      const res  = await fetch(`${baseUrl}/ngo/helpandsupport/messages/${ticket.id}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      if (data.success) setMessages(data.messages);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoadingMsgs(false);
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const res  = await fetch(`${baseUrl}/ngo/helpandsupport/message`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: ticket.id, message: newMsg.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        setNewMsg('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ngo-chat-wrap">
      <div className="ngo-chat-header">
        <span>💬 Conversation</span>
        <span className="ngo-chat-count">{messages.length} messages</span>
      </div>

      <div className="ngo-chat-messages">
        {loadingMsgs ? (
          <div className="ngo-chat-loading"><Loader size={18} className="ngo-spin" /> Loading…</div>
        ) : messages.length === 0 ? (
          <div className="ngo-chat-empty">No messages yet. Start the conversation!</div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`ngo-chat-bubble ${msg.sender_type === 'ngo' ? 'ngo-chat-bubble--ngo' : 'ngo-chat-bubble--donor'}`}
            >
              <div className="ngo-chat-bubble-label">
                {msg.sender_type === 'ngo' ? '🏢 NGO' : '👤 Donor'}
              </div>
              <div className="ngo-chat-bubble-text">{msg.message}</div>
              <div className="ngo-chat-bubble-time">{formatTime(msg.created_at)}</div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* NGO hamesha message kar sakta hai — closed ho ya na ho */}
      <div className="ngo-chat-input-wrap">
        <textarea
          className="ngo-chat-input"
          placeholder="Type your reply…"
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
        />
        <button
          className="ngo-chat-send-btn"
          onClick={handleSend}
          disabled={sending || !newMsg.trim()}
        >
          {sending ? <Loader size={16} className="ngo-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}

/* ─── TICKET DETAIL MODAL ─────────────────────── */
function TicketModal({ ticket, onClose, onCloseTicket, closing, baseUrl }) {
  return (
    <div className="ngo-hs-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ngo-hs-modal ngo-hs-modal--wide">

        <div className="ngo-hs-modal-header">
          <div>
            <p className="ngo-hs-modal-label mb-1">Support Ticket #{ticket.id}</p>
            <h2 className="ngo-hs-modal-title">{ticket.subject}</h2>
          </div>
          <button className="ngo-hs-close-btn" onClick={onClose}>
            <X size={22} />
          </button>
        </div>

        <div className="ngo-hs-modal-meta">
          <div className="ngo-hs-meta-item">
            <span className="ngo-hs-meta-label">Donor Name</span>
            <span className="ngo-hs-meta-value">{ticket.donor?.fullname || "—"}</span>
          </div>
          <div className="ngo-hs-meta-item">
            <span className="ngo-hs-meta-label">Donor Email</span>
            <span className="ngo-hs-meta-value">{ticket.donor?.email || "—"}</span>
          </div>
          <div className="ngo-hs-meta-item">
            <span className="ngo-hs-meta-label">Category</span>
            <span className="ngo-hs-meta-value">{ticket.category}</span>
          </div>
          <div className="ngo-hs-meta-item">
            <span className="ngo-hs-meta-label">Status</span>
            <span className="ngo-hs-meta-value"><StatusBadge status={ticket.status} /></span>
          </div>
          <div className="ngo-hs-meta-item">
            <span className="ngo-hs-meta-label">Submitted</span>
            <span className="ngo-hs-meta-value">{formatDate(ticket.created_at)}</span>
          </div>
          {ticket.closed_at && (
            <div className="ngo-hs-meta-item">
              <span className="ngo-hs-meta-label">Closed</span>
              <span className="ngo-hs-meta-value">{formatDate(ticket.closed_at)}</span>
            </div>
          )}
        </div>

        {/* Donor ki original description */}
        <div className="ngo-hs-modal-body">
          <p className="ngo-hs-desc-label">Donor's Original Message</p>
          <div className="ngo-hs-desc-box">{ticket.description}</div>
        </div>

        {/* Chat Box */}
        <ChatBox ticket={ticket} baseUrl={baseUrl} />

        <div className="ngo-hs-modal-footer">
          <button className="ngo-hs-btn-ghost" onClick={onClose}>Close</button>

          {ticket.status !== 'closed' && (
            <button
              className="ngo-hs-btn-close-ticket d-inline-flex align-items-center gap-2"
              onClick={() => onCloseTicket(ticket.id)}
              disabled={closing}
            >
              {closing
                ? <><Loader size={16} className="ngo-spin" /> Closing…</>
                : <><CheckCircle size={16} /> Mark as Resolved</>
              }
            </button>
          )}

          {ticket.status === 'closed' && (
            <div className="ngo-hs-resolved-notice d-flex align-items-center gap-2">
              <CheckCircle size={16} /> Ticket is resolved and closed
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ──────────────────────────── */
const Ngomessage = () => {
  const { baseUrl } = useContext(AppContext);

  const [tickets, setTickets]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [statusFilter, setStatusFilter]     = useState('all');
  const [searchQuery, setSearchQuery]       = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [closing, setClosing]               = useState(false);
  const [toast, setToast]                   = useState(null);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? `${baseUrl}/ngo/helpandsupport/viewAll`
        : `${baseUrl}/ngo/helpandsupport/viewAll?status=${statusFilter}`;

      const res  = await fetch(url, { headers: { Accept: "application/json" } });
      const data = await res.json();
      if (data.success) setTickets(data.tickets);
    } catch (err) {
      console.error("Failed to fetch tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTicket = async (ticket) => {
    try {
      const res  = await fetch(`${baseUrl}/ngo/helpandsupport/view/${ticket.id}`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      if (data.success) {
        setTickets(prev => prev.map(t => t.id === ticket.id ? data.ticket : t));
        setSelectedTicket(data.ticket);
      }
    } catch (err) {
      console.error("Failed to open ticket:", err);
      setSelectedTicket(ticket);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    setClosing(true);
    try {
      const res  = await fetch(`${baseUrl}/ngo/helpandsupport/close/${ticketId}`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) {
        setTickets(prev => prev.map(t => t.id === ticketId ? data.ticket : t));
        setSelectedTicket(data.ticket);
        setToast({ msg: `Ticket #${ticketId} marked as resolved.`, type: "success" });
      } else {
        setToast({ msg: data.message || "Could not close ticket.", type: "error" });
      }
    } catch (err) {
      setToast({ msg: "Network error. Please try again.", type: "error" });
    } finally {
      setClosing(false);
    }
  };

  const filteredTickets = tickets.filter(t =>
    t.subject?.toLowerCase().includes(searchQuery.toLowerCase())   ||
    t.category?.toLowerCase().includes(searchQuery.toLowerCase())  ||
    t.donor?.fullname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filterTabs = [
    { id: 'all',     label: 'All',     count: tickets.length },
    { id: 'pending', label: 'Pending', count: tickets.filter(t => t.status === 'pending').length },
    { id: 'open',    label: 'Open',    count: tickets.filter(t => t.status === 'open').length },
    { id: 'closed',  label: 'Closed',  count: tickets.filter(t => t.status === 'closed').length },
  ];

  return (
    <NGOMasterLayout>
      <div className="ngo-hs-root">

        <div className="ngo-hs-header">
          <h1 className="ngo-hs-title">Support Tickets</h1>
          <p className="ngo-hs-subtitle">Manage and resolve donor support requests</p>

          <div className="ngo-hs-search-wrap">
            <Search size={18} className="ngo-hs-search-icon" />
            <input
              type="text"
              placeholder="Search by subject, category, or donor name…"
              className="ngo-hs-search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="ngo-hs-search-clear" onClick={() => setSearchQuery('')}>
                <X size={16} />
              </button>
            )}
          </div>

          <div className="ngo-hs-filter-tabs">
            {filterTabs.map(tab => (
              <button
                key={tab.id}
                className={`ngo-hs-tab${statusFilter === tab.id ? ' active' : ''}`}
                onClick={() => setStatusFilter(tab.id)}
              >
                {tab.label}
                <span className="ngo-hs-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="ngo-hs-list">
          {loading ? (
            <div className="ngo-hs-empty">
              <Loader size={28} className="ngo-spin" />
              <p>Loading tickets…</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="ngo-hs-empty">
              <Inbox size={32} />
              <p>No tickets found</p>
            </div>
          ) : (
            filteredTickets.map(ticket => (
              <div
                key={ticket.id}
                className={`ngo-hs-card${ticket.status === 'pending' ? ' unread' : ''}`}
                onClick={() => handleOpenTicket(ticket)}
              >
                <div className="ngo-hs-card-left">
                  <span className={`ngo-hs-dot ngo-hs-dot--${ticket.status}`} />
                </div>
                <div className="ngo-hs-card-body">
                  <div className="ngo-hs-card-top">
                    <div className="ngo-hs-card-info">
                      <span className="ngo-hs-card-id">#{ticket.id}</span>
                      <span className="ngo-hs-card-subject">{ticket.subject}</span>
                    </div>
                    <StatusBadge status={ticket.status} />
                  </div>
                  <div className="ngo-hs-card-meta">
                    <span className="ngo-hs-card-donor">👤 {ticket.donor?.fullname || "Unknown Donor"}</span>
                    <span className="ngo-hs-card-sep">·</span>
                    <span className="ngo-hs-card-cat">{ticket.category}</span>
                    <span className="ngo-hs-card-sep">·</span>
                    <span className="ngo-hs-card-date">{formatDate(ticket.created_at)}</span>
                  </div>
                  <p className="ngo-hs-card-preview">
                    {ticket.description?.slice(0, 120)}
                    {ticket.description?.length > 120 ? "…" : ""}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedTicket && (
          <TicketModal
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
            onCloseTicket={handleCloseTicket}
            closing={closing}
            baseUrl={baseUrl}
          />
        )}

        {toast && (
          <div className={`ngo-hs-toast ngo-hs-toast--${toast.type}`}>
            {toast.type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {toast.msg}
            <button onClick={() => setToast(null)}><X size={14} /></button>
          </div>
        )}
      </div>
    </NGOMasterLayout>
  );
};

export default Ngomessage;